import React from "react";
import { AlertTriangle, CheckCircle2, XCircle, ShieldAlert, ArrowRight } from "lucide-react";

export const fmtInt = (n) => (Number(n) || 0).toLocaleString("es-419");

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-419", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const PILL = "inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] whitespace-nowrap";

export function ActiveBadge({ active, testid }) {
  return active ? (
    <span className={`${PILL} border-emerald-300 bg-emerald-50 text-emerald-800`} data-testid={testid}>
      <CheckCircle2 className="h-3 w-3" /> Activa
    </span>
  ) : (
    <span className={`${PILL} border-red-300 bg-red-50 text-red-700`} data-testid={testid}>
      <XCircle className="h-3 w-3" /> Inactiva
    </span>
  );
}

export function AgentStateBadge({ active, testid }) {
  return active ? (
    <span className={`${PILL} border-emerald-300 bg-emerald-50 text-emerald-800`} data-testid={testid}>Activo</span>
  ) : (
    <span className={`${PILL} border-red-300 bg-red-50 text-red-700`} data-testid={testid}>Inactivo</span>
  );
}

export function DemoBadge() {
  return <span className={`${PILL} border-amber-300 bg-amber-50 text-amber-800`}>Demo</span>;
}

const ALERT_STYLE = {
  none: ["border-slate-200 bg-slate-50 text-slate-500", "Sin alertas"],
  warning: ["border-amber-400 bg-amber-50 text-amber-800", "Aviso 80 %"],
  exhausted: ["border-red-400 bg-red-50 text-red-700", "Agotado"],
};

export function TokenAlertBadge({ level = "none", testid }) {
  const [cls, label] = ALERT_STYLE[level] || ALERT_STYLE.none;
  return (
    <span className={`${PILL} ${cls}`} data-testid={testid} title="Alerta de consumo de tokens del mes (UTC)">
      {level !== "none" && <AlertTriangle className="h-3 w-3" />} {label}
    </span>
  );
}

/** Level from a usage percentage and backend status (same thresholds as the backend: 80 % and 100 %). */
export function usageLevel(pct, status) {
  if (status === "limite_alcanzado" || (pct != null && pct >= 100)) return "exhausted";
  if (pct != null && pct >= 80) return "warning";
  return "ok";
}

export function UsageBar({ used = 0, limit = 0, pct = null, status = "ok", testid }) {
  const level = usageLevel(pct, status);
  const width = Math.max(0, Math.min(100, Number(pct) || 0));
  const bar = level === "exhausted" ? "bg-red-500" : level === "warning" ? "bg-amber-500" : "bg-teal-600";
  const label = level === "exhausted" ? "Agotado (100 %)" : level === "warning" ? "Aviso (80 %)" : "Normal";
  const labelCls = level === "exhausted" ? "text-red-700" : level === "warning" ? "text-amber-700" : "text-teal-700";
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-[11px]">
        <span className="font-mono text-slate-700">
          {fmtInt(used)} / {fmtInt(limit)} tokens
        </span>
        <span className={`font-mono uppercase tracking-[0.12em] ${labelCls}`}>
          {pct == null ? "—" : `${Number(pct).toLocaleString("es-419")} %`} · {label}
        </span>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-sm bg-slate-100">
        <div className={`h-full ${bar}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

/** Banner with the token alerts of a company (admin record and master panel). No email to Inviktus. */
export function TokenAlertsBanner({ level, alerts, testid = "token-alerts-banner", onGoKeys }) {
  if (!alerts || !alerts.length || level === "none") return null;
  const exhausted = level === "exhausted";
  return (
    <div
      className={`mt-6 border p-4 ${exhausted ? "border-red-300 bg-red-50 text-red-900" : "border-amber-400 bg-amber-50 text-amber-900"}`}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            {exhausted
              ? "Hay claves de IA con el límite mensual agotado: los agentes afectados no responden."
              : "Hay claves de IA que superaron el 80 % de su límite mensual."}
          </p>
          <ul className="mt-2 space-y-1 text-[13px]">
            {alerts.map((a, i) => (
              <li key={`${a.scope}-${a.agent_slug || "company"}-${i}`} className="flex flex-wrap items-center gap-2"
                  data-testid={`token-alert-item-${a.agent_slug || "company"}`}>
                <TokenAlertBadge level={a.level} />
                <span className="font-medium">
                  {a.scope === "company" ? "Clave genérica de la empresa" : `Agente: ${a.agent_name || a.agent_slug}`}
                </span>
                <span className="font-mono text-[12px]">
                  {Number(a.percent).toLocaleString("es-419")} % usado · límite {fmtInt(a.limit)} tokens ({a.month}, UTC)
                  {a.estimated ? " · incluye estimaciones" : ""}
                </span>
              </li>
            ))}
          </ul>
          {onGoKeys && (
            <button onClick={onGoKeys} className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium underline"
                    data-testid={`${testid}-go-keys`}>
              Revisar claves y límites <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function ChainExplainer({ forClient = false }) {
  return (
    <div className="border border-slate-200 bg-slate-50 p-4 text-[13px] leading-relaxed text-slate-700" data-testid="llm-chain-explainer">
      <p className="font-semibold text-slate-900">Cómo se elige la clave de IA de cada agente</p>
      <ol className="mt-2 list-decimal space-y-1 pl-5">
        <li>Si el agente tiene su <strong>clave propia</strong> activa y con saldo de su límite mensual, se usa esa. Si falla (autenticación, saldo, cuota o caída del proveedor) y tiene <strong>clave de backup</strong>, responde el backup, que consume del mismo límite.</li>
        <li>Si no, se usa la <strong>clave genérica</strong> de la empresa, con su propio límite mensual (y su backup opcional).</li>
        <li>Si ninguna está disponible, el agente queda <strong>bloqueado</strong> y avisa al usuario que contacte al responsable de su empresa. No se gasta nada.</li>
      </ol>
      <p className="mt-2">
        {forClient
          ? "Las claves de Inviktus nunca se usan para los agentes de su empresa: el consumo corre siempre por sus propias claves. Puede elegir OpenRouter, Anthropic, DeepSeek, Kimi, Qwen y los demás proveedores principales."
          : "Las claves de Inviktus (Admin → Claves LLM) nunca se usan para agentes de clientes (solo para la entrevista, el informe, la generación de agentes y las demos)."}
        {" "}El mes se cuenta en horario UTC. Al 80 % y al 100 % se envía un aviso al usuario master de la empresa.
        Cerca del límite, la última respuesta se acorta para no superarlo.
      </p>
    </div>
  );
}

export function Field({ label, hint, children, required }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600">
        {label} {required && <span className="text-red-600">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      {hint && <span className="mt-1 block text-[11px] text-slate-500">{hint}</span>}
    </label>
  );
}

export const errDetail = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" · ") || fallback;
  return fallback;
};
